/**
 * EditPaymentModal Component
 * Modal for editing an existing payment record
 */

import React, { useState, useEffect, useRef } from 'react';
import { paymentMilestoneAPI, projectFilesAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';

// Payment method display mapping
const PAYMENT_METHODS = {
  bank_transfer: 'Bank Transfer',
  cash: 'Cash',
  cheque: 'Cheque',
  credit_card: 'Credit Card',
  online_payment: 'Online Payment',
  other: 'Other'
};

const EditPaymentModal = ({ isOpen, onClose, onSuccess, payment, milestone, projectId, summary }) => {
  const [formData, setFormData] = useState({
    amount: '',
    paymentDate: '',
    paymentMethod: '',
    referenceNumber: '',
    notes: ''
  });
  const [existingReceipts, setExistingReceipts] = useState([]);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Calculate impact of deleting this payment
  const paymentAmount = parseFloat(payment?.amount) || 0;
  const totalPaid = parseFloat(summary?.totalPaidAmount) || 0;
  const packagePrice = parseFloat(summary?.totalPackagePrice) || 0;
  const siteEntryCreated = summary?.siteEntryCreated || false;

  const newTotalAfterDelete = totalPaid - paymentAmount;
  const newPercentageAfterDelete = packagePrice > 0 ? (newTotalAfterDelete / packagePrice) * 100 : 0;
  const wouldDropBelow50 = totalPaid >= (packagePrice * 0.5) && newTotalAfterDelete < (packagePrice * 0.5);
  const showSiteWarning = siteEntryCreated && wouldDropBelow50;

  // Initialize form data when payment changes
  useEffect(() => {
    if (payment) {
      setFormData({
        amount: payment.amount || '',
        paymentDate: payment.paymentDate ? payment.paymentDate.split('T')[0] : '',
        paymentMethod: payment.paymentMethod || '',
        referenceNumber: payment.referenceNumber || '',
        notes: payment.notes || ''
      });
      setExistingReceipts(Array.isArray(payment.receiptFiles) ? [...payment.receiptFiles] : []);
      setPendingReceipts([]);
    }
  }, [payment]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024;
      if (!isValidType) toast.error(`${file.name}: Only images and PDFs are allowed`);
      if (!isValidSize) toast.error(`${file.name}: File size must be under 10MB`);
      return isValidType && isValidSize;
    });
    setPendingReceipts(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeExistingReceipt = (index) => {
    setExistingReceipts(prev => prev.filter((_, i) => i !== index));
  };

  const removePendingReceipt = (index) => {
    setPendingReceipts(prev => prev.filter((_, i) => i !== index));
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (!formData.paymentDate) {
      toast.error('Please select a payment date');
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);

    try {
      const uploadedFiles = [];
      if (pendingReceipts.length > 0) {
        for (let i = 0; i < pendingReceipts.length; i++) {
          const file = pendingReceipts[i];
          const fd = new FormData();
          fd.append('file', file);
          fd.append('projectId', projectId);
          fd.append('fileType', 'payment_receipt');
          const uploadRes = await projectFilesAPI.upload(fd);
          if (uploadRes.data.data) {
            uploadedFiles.push({
              fileId: uploadRes.data.data.id,
              fileName: uploadRes.data.data.originalName,
              filePath: uploadRes.data.data.filePath
            });
          }
          setUploadProgress(Math.round(((i + 1) / pendingReceipts.length) * 100));
        }
      }

      await paymentMilestoneAPI.updatePayment(projectId, milestone.id, payment.id, {
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod || null,
        referenceNumber: formData.referenceNumber || null,
        notes: formData.notes || null,
        receiptFiles: [...existingReceipts, ...uploadedFiles]
      });

      toast.success('Payment updated successfully');
      onSuccess();
    } catch (err) {
      console.error('Error updating payment:', err);
      toast.error(err.message || 'Failed to update payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await paymentMilestoneAPI.deletePayment(projectId, milestone.id, payment.id);
      toast.success('Payment deleted successfully');
      onSuccess();
    } catch (err) {
      console.error('Error deleting payment:', err);
      toast.error(err.message || 'Failed to delete payment');
    } finally {
      setSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen || !payment) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      {/* Backdrop - only for desktop clicking outside */}
      <div
        className="absolute inset-0"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          {/* Header - Sticky */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Edit Payment
            </h3>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{milestone?.milestoneName}</p>
          </div>

          {/* Content - Scrollable */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            {/* Current Payment Info */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Current Amount:</span>
                <span className="font-medium">{formatCurrency(payment.amount)}</span>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount (RM) *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                  autoFocus
                />
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  disabled={submitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  disabled={submitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                >
                  <option value="">Select method</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="online_payment">Online Payment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Reference Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={formData.referenceNumber}
                  onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                  disabled={submitting}
                  placeholder="Transaction reference"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  disabled={submitting}
                  rows={2}
                  placeholder="Additional notes"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-base touch-manipulation"
                />
              </div>

              {/* Receipts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Receipts
                </label>

                {existingReceipts.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {existingReceipts.map((file, index) => (
                      <div key={`existing-${file.fileId || index}`} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {file.filePath ? (
                            <a
                              href={file.filePath}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline truncate"
                            >
                              {file.fileName || 'Receipt'}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-700 truncate">{file.fileName || 'Receipt'}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeExistingReceipt(index)}
                          disabled={submitting}
                          className="text-red-500 hover:text-red-700 disabled:opacity-50 ml-2 p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                          aria-label="Remove receipt"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  onClick={() => !submitting && fileInputRef.current?.click()}
                  className={`border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 active:bg-blue-100 transition-colors min-h-[80px] touch-manipulation ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600">Tap to add receipt</p>
                  <p className="text-xs text-gray-400 mt-1">Images or PDF, max 10MB each</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {pendingReceipts.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {pendingReceipts.map((file, index) => (
                      <div key={`pending-${index}`} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePendingReceipt(index)}
                          disabled={submitting}
                          className="text-red-500 hover:text-red-700 disabled:opacity-50 ml-2 p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                          aria-label="Remove pending receipt"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {submitting && uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Uploading...</span>
                      <span className="font-medium">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Delete Section */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                {!showDeleteConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={submitting}
                    className="text-red-600 hover:text-red-700 text-sm font-medium min-h-[44px] touch-manipulation"
                  >
                    Delete this payment
                  </button>
                ) : (
                  <div className="bg-red-50 rounded-lg p-3 sm:p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">Delete this payment?</p>
                    <p className="text-sm text-red-700 mb-3">
                      This will permanently delete the payment of {formatCurrency(payment.amount)}.
                    </p>
                    {showSiteWarning && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                        <p className="text-xs text-amber-800">
                          <strong>Warning:</strong> A site entry has already been created.
                          Deleting this payment will drop progress to {newPercentageAfterDelete.toFixed(1)}%
                          (below 50%), but the site entry will remain active.
                        </p>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={submitting}
                        className="w-full sm:w-auto px-4 py-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
                      >
                        {submitting ? 'Deleting...' : 'Yes, Delete'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={submitting}
                        className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentModal;
